"use client";
import {
  CheckOutlined,
  CloseOutlined,
  FolderOutlined,
  InfoCircleOutlined,
  LockOutlined,
} from "@ant-design/icons";
import ChevronDown from "@untitled-ui/icons-react/build/esm/ChevronDown";
import { Button, Col, Form, Input, Row, Select, Tooltip } from "antd";
import { useEffect } from "react";

const sectionHeaderStyle = {
  fontSize: 13,
  fontWeight: 600,
  color: "#475467",
  letterSpacing: "0.05em",
  marginBottom: 16,
};

const sectionDividerStyle = {
  borderTop: "1px dashed #e5e7eb",
  paddingTop: 24,
  marginTop: 24,
};

/**
 * The maker-checker approval chain. Each node is a review stage that is either
 *  - approved  : the reviewer signed off and forwarded to the next stage
 *  - active    : the stage currently awaiting a decision (only ever one)
 *  - waiting   : a downstream stage that is still locked
 *  - declined  : the reviewer rejected the application (chain stops here)
 */
const DEFAULT_CHAIN = [
  {
    id: "officer",
    title: "Loan Officer Recommendation",
    role: "Loan Officer",
    assignee: "E. Koffi",
    initials: "EK",
    due: "29 May 2026",
    status: "active",
  },
  {
    id: "branch",
    title: "Branch Manager Review",
    role: "Branch Manager",
    assignee: "O. Adesanya",
    initials: "BM",
    due: "30 May 2026",
    status: "waiting",
  },
  {
    id: "committee",
    title: "Credit Committee Approval",
    role: "Credit Committee",
    assignee: "Committee Chair",
    initials: "CC",
    due: "02 Jun 2026",
    status: "waiting",
  },
  {
    id: "risk",
    title: "Risk & Compliance Sign-off",
    role: "Risk & Compliance",
    assignee: "F. Mensah",
    initials: "RC",
    due: "03 Jun 2026",
    status: "waiting",
  },
  {
    id: "disbursement",
    title: "Disbursement Authorisation",
    role: "Head of Operations",
    assignee: "L. Okonkwo",
    initials: "DA",
    due: "04 Jun 2026",
    status: "waiting",
  },
];

const DEFAULT_THREAD = [];

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const pad = (n) => String(n).padStart(2, "0");

const formatNow = () => {
  const d = new Date();
  return `${pad(d.getDate())} ${MONTHS[d.getMonth()]} ${d.getFullYear()} · ${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};

const NODE_STYLES = {
  approved: { background: "#079455", border: "2px solid #079455", color: "white" },
  active: { background: "#15CCAE", border: "2px solid #15CCAE", color: "white" },
  declined: { background: "#DC2626", border: "2px solid #DC2626", color: "white" },
  waiting: { background: "#F2F4F7", border: "2px solid #E4E7EC", color: "#98A2B3" },
};

const STATUS_BADGE = {
  approved: { text: "Approved", color: "#067647", bg: "#ECFDF3" },
  recommended: { text: "Recommended", color: "#067647", bg: "#ECFDF3" },
  authorised: { text: "Authorised", color: "#067647", bg: "#ECFDF3" },
  declined: { text: "Declined", color: "#B42318", bg: "#FEF3F2" },
};

const ApprovalWorkflow = (props) => {
  const { onFinish, form, initialValues, savedState, onChainChange } = props;

  const chain = savedState?.chain || DEFAULT_CHAIN;
  const thread = savedState?.thread || DEFAULT_THREAD;

  const activeIndex = chain.findIndex((stage) => stage.status === "active");
  const activeStage = activeIndex >= 0 ? chain[activeIndex] : null;
  const isFinalStage = activeIndex === chain.length - 1;
  const declined = chain.some((stage) => stage.status === "declined");
  const complete = chain.every((stage) => stage.status === "approved");

  // Keep the parent (NewLoanOrigination) in sync so it can gate the
  // "Generate offer letter" step on the whole chain being approved.
  useEffect(() => {
    if (onChainChange) {
      onChainChange({ chain, thread, complete, declined });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chain, thread, complete, declined]);

  const persist = (nextChain, nextThread) => {
    if (onChainChange) {
      const done = nextChain.every((stage) => stage.status === "approved");
      const stopped = nextChain.some((stage) => stage.status === "declined");
      onChainChange({
        chain: nextChain,
        thread: nextThread,
        complete: done,
        declined: stopped,
      });
    }
  };

  const submitDecision = () => {
    if (activeIndex < 0) return;

    const type = form.getFieldValue("stageDecision");
    const comment = (form.getFieldValue("reviewComments") || "").trim();

    const errors = [];
    if (!type) {
      errors.push({
        name: "stageDecision",
        errors: ["Select a decision to continue."],
      });
    }
    if (!comment) {
      errors.push({
        name: "reviewComments",
        errors: ["Add review comments to record your decision."],
      });
    }
    if (errors.length) {
      form.setFields(errors);
      return;
    }

    applyDecision(type, comment);
  };

  const applyDecision = (type, comment) => {
    if (activeIndex < 0) return;

    const stage = chain[activeIndex];
    const stamp = formatNow();
    const nextStage = chain[activeIndex + 1];
    const prevStage = chain[activeIndex - 1];

    const nextChain = chain.map((item) => ({ ...item }));

    let verb;
    let badge;

    if (type === "approve") {
      const isFirstStage = activeIndex === 0;
      nextChain[activeIndex] = {
        ...nextChain[activeIndex],
        status: "approved",
        decision: isFinalStage
          ? "authorised"
          : isFirstStage
          ? "recommended"
          : "approved",
        comment,
        decidedAt: stamp,
      };
      if (!isFinalStage) {
        nextChain[activeIndex + 1] = {
          ...nextChain[activeIndex + 1],
          status: "active",
        };
      }
      verb = isFinalStage
        ? "authorised the application for disbursement."
        : isFirstStage
        ? `recommended the application and forwarded to ${
            nextStage?.title || "the next stage"
          }.`
        : `approved and forwarded to ${nextStage?.title || "the next stage"}.`;
      badge = isFinalStage
        ? { text: "Authorised", color: "#067647", bg: "#ECFDF3" }
        : isFirstStage
        ? { text: "Recommended", color: "#067647", bg: "#ECFDF3" }
        : { text: "Approved", color: "#067647", bg: "#ECFDF3" };
    } else if (type === "decline") {
      nextChain[activeIndex] = {
        ...nextChain[activeIndex],
        status: "declined",
        decision: "declined",
        comment,
        decidedAt: stamp,
      };
      verb = "declined the application.";
      badge = { text: "Declined", color: "#B42318", bg: "#FEF3F2" };
    } else {
      // return for revision -> re-open the previous stage
      nextChain[activeIndex] = {
        ...nextChain[activeIndex],
        status: "waiting",
        decision: undefined,
        comment: undefined,
        decidedAt: undefined,
      };
      nextChain[activeIndex - 1] = {
        ...nextChain[activeIndex - 1],
        status: "active",
        decision: undefined,
        decidedAt: undefined,
      };
      verb = `returned the application to ${
        prevStage?.title || "the previous stage"
      } for revision.`;
      badge = { text: "Returned", color: "#B54708", bg: "#FFFAEB" };
    }

    const nextThread = [
      ...thread,
      {
        id: `${stage.id}-${Date.now()}`,
        initials: stage.initials,
        name: stage.assignee,
        role: stage.role,
        comment: `${verb} ${comment}`,
        timestamp: stamp,
        badge,
      },
    ];

    form.setFieldsValue({ reviewComments: "", stageDecision: undefined });
    persist(nextChain, nextThread);
  };

  const reopenDeclined = () => {
    const idx = chain.findIndex((stage) => stage.status === "declined");
    if (idx < 0) return;
    const nextChain = chain.map((item) => ({ ...item }));
    nextChain[idx] = {
      ...nextChain[idx],
      status: "active",
      decision: undefined,
      comment: undefined,
      decidedAt: undefined,
    };
    const nextThread = [
      ...thread,
      {
        id: `reopen-${Date.now()}`,
        initials: "SY",
        name: "System",
        role: "Workflow",
        comment: `${chain[idx].title} re-opened for further review.`,
        timestamp: formatNow(),
        badge: { text: "Re-opened", color: "#344054", bg: "#F2F4F7" },
      },
    ];
    persist(nextChain, nextThread);
  };

  const getNodeContent = (node, index) => {
    if (node.status === "approved") {
      return <CheckOutlined style={{ fontSize: 18, color: "white" }} />;
    }
    if (node.status === "declined") {
      return <CloseOutlined style={{ fontSize: 18, color: "white" }} />;
    }
    return (
      <span
        style={{
          fontSize: 18,
          fontWeight: node.status === "active" ? 700 : 600,
          color: node.status === "active" ? "white" : "#98A2B3",
        }}
      >
        {index + 1}
      </span>
    );
  };

  const completedStages = chain.filter(
    (stage) => stage.status === "approved" || stage.status === "declined"
  );

  const decisionOptions = [
    {
      value: "approve",
      label:
        activeIndex === 0
          ? "Recommend and forward"
          : isFinalStage
          ? "Authorise and release"
          : "Approve and forward",
    },
    ...(activeIndex > 0
      ? [{ value: "return", label: "Return for revision" }]
      : []),
    { value: "decline", label: "Decline application" },
  ];

  return (
    <Form
      className="custom-form"
      layout="vertical"
      onFinish={onFinish}
      form={form}
      initialValues={initialValues}
    >
      {/* Approval Chain */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          position: "relative",
          padding: "0 16px",
          marginBottom: 32,
        }}
      >
        {chain.map((node, index) => (
          <div
            key={node.id}
            style={{ textAlign: "center", flex: 1, position: "relative" }}
          >
            {index > 0 && (
              <div
                style={{
                  position: "absolute",
                  top: 24,
                  left: -50,
                  right: "50%",
                  height: 2,
                  background:
                    chain[index - 1].status === "approved"
                      ? "#079455"
                      : "#E4E7EC",
                  zIndex: 0,
                  transform: "translateX(24px)",
                  width: "calc(100% - 48px)",
                }}
              />
            )}
            <div
              style={{
                width: node.status === "active" ? 56 : 48,
                height: node.status === "active" ? 56 : 48,
                borderRadius: "50%",
                margin: "0 auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                zIndex: 1,
                ...(NODE_STYLES[node.status] || NODE_STYLES.waiting),
              }}
            >
              {getNodeContent(node, index)}
            </div>
            <div
              style={{
                fontSize: 12,
                fontWeight: 600,
                marginTop: 8,
                color: node.status === "waiting" ? "#98A2B3" : "#344054",
                lineHeight: 1.3,
              }}
            >
              {node.title}
            </div>
            <div style={{ fontSize: 11, color: "#98A2B3", marginTop: 2 }}>
              {node.status === "approved"
                ? STATUS_BADGE[node.decision]?.text || "Approved"
                : node.status === "active"
                ? "In review"
                : node.status === "declined"
                ? "Declined"
                : node.id === "disbursement"
                ? "Final"
                : "Waiting"}
            </div>
          </div>
        ))}
      </div>

      {/* Decisions already recorded */}
      {completedStages.map((stage) => {
        const badge = STATUS_BADGE[stage.decision] || STATUS_BADGE.approved;
        return (
          <div
            key={`summary-${stage.id}`}
            style={{
              display: "flex",
              gap: 12,
              borderRadius: 12,
              border: "1px solid #e5e7eb",
              background: "#FCFCFD",
              padding: 16,
              marginBottom: 12,
            }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                background: stage.status === "declined" ? "#DC2626" : "#079455",
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 12,
                fontWeight: 600,
                flexShrink: 0,
              }}
            >
              {stage.initials}
            </div>
            <div style={{ flex: 1 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  flexWrap: "wrap",
                }}
              >
                <span style={{ fontWeight: 600, fontSize: 14 }}>
                  {stage.title}
                </span>
                <span
                  style={{
                    background: badge.bg,
                    color: badge.color,
                    padding: "2px 10px",
                    borderRadius: 12,
                    fontSize: 11,
                    fontWeight: 600,
                  }}
                >
                  {badge.text}
                </span>
              </div>
              <div style={{ fontSize: 12, color: "#98A2B3", marginTop: 2 }}>
                {stage.assignee} &middot; {stage.decidedAt}
              </div>
              {stage.comment && (
                <div
                  style={{
                    fontSize: 13,
                    color: "#475467",
                    marginTop: 6,
                    lineHeight: 1.5,
                  }}
                >
                  {stage.comment}
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* Current Reviewer Card */}
      {activeStage && (
        <div
          style={{
            borderRadius: 12,
            border: "1px solid #e5e7eb",
            padding: 24,
            marginTop: 12,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 20,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  background: "#15CCAE",
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                {activeStage.initials}
              </div>
              <div>
                <div style={{ fontWeight: 600, fontSize: 15 }}>
                  {activeStage.title}
                </div>
                <div style={{ fontSize: 13, color: "#15CCAE" }}>
                  Assigned to: {activeStage.assignee} &middot; Due:{" "}
                  {activeStage.due}
                </div>
              </div>
            </div>
            <div
              style={{
                background: "#FEF3C7",
                color: "#92400E",
                padding: "4px 12px",
                borderRadius: 16,
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              Stage {activeIndex + 1} of {chain.length}
            </div>
          </div>

          <Row>
            <Col span={24} style={{ padding: "0px 8px" }}>
              <Form.Item
                label={
                  <span>
                    {activeIndex === 0 ? "Recommendation" : `${activeStage.role} Decision`}
                    <Tooltip title="Select the outcome of this review stage">
                      <InfoCircleOutlined
                        style={{ marginLeft: "4px", color: "#667085" }}
                      />
                    </Tooltip>
                  </span>
                }
                name="stageDecision"
              >
                <Select
                  placeholder="Select decision..."
                  style={{ height: 44 }}
                  suffixIcon={<ChevronDown color="#667085" width={20} />}
                  options={decisionOptions}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row>
            <Col span={24} style={{ padding: "0px 8px" }}>
              <Form.Item
                label={
                  <span>
                    Comments
                    <Tooltip title="Every approval decision must be recorded with a rationale">
                      <InfoCircleOutlined
                        style={{ marginLeft: "4px", color: "#667085" }}
                      />
                    </Tooltip>
                  </span>
                }
                name="reviewComments"
              >
                <Input.TextArea
                  className="bond-input"
                  style={{ height: 88, resize: "none" }}
                  placeholder="Add review comments..."
                />
              </Form.Item>
            </Col>
          </Row>

          <div style={{ display: "flex", gap: 12, padding: "0px 8px" }}>
            <Button
              onClick={submitDecision}
              style={{
                background: "#15CCAE",
                color: "white",
                border: "1px solid #15CCAE",
                borderRadius: 8,
                height: 40,
                fontWeight: 600,
              }}
            >
              Submit decision
            </Button>
          </div>
        </div>
      )}

      {/* Chain complete */}
      {complete && (
        <div
          style={{
            borderRadius: 12,
            border: "1px solid #A6F4C5",
            background: "#F6FEF9",
            padding: 20,
            marginTop: 12,
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <CheckOutlined style={{ fontSize: 20, color: "#079455" }} />
          <div>
            <div style={{ fontWeight: 600, fontSize: 14, color: "#05603A" }}>
              Approval chain complete
            </div>
            <div style={{ fontSize: 13, color: "#475467", marginTop: 2 }}>
              All stages have signed off. You can now generate the offer letter.
            </div>
          </div>
        </div>
      )}

      {/* Chain stopped */}
      {declined && (
        <div
          style={{
            borderRadius: 12,
            border: "1px solid #FECDCA",
            background: "#FFFBFA",
            padding: 20,
            marginTop: 12,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <CloseOutlined style={{ fontSize: 20, color: "#D92D20" }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 14, color: "#912018" }}>
                Application declined at{" "}
                {chain.find((s) => s.status === "declined")?.title}
              </div>
              <div style={{ fontSize: 13, color: "#475467", marginTop: 2 }}>
                The approval chain has stopped. Re-open the stage to continue the
                review.
              </div>
            </div>
            <Button
              onClick={reopenDeclined}
              style={{
                background: "white",
                color: "#344054",
                border: "1px solid #D0D5DD",
                borderRadius: 8,
                height: 36,
                fontWeight: 600,
              }}
            >
              Re-open review
            </Button>
          </div>
        </div>
      )}

      {/* Locked downstream stages hint */}
      {activeStage && activeIndex < chain.length - 1 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 12,
            color: "#98A2B3",
            marginTop: 12,
            padding: "0px 8px",
          }}
        >
          <LockOutlined />
          {chain.length - activeIndex - 1} downstream stage
          {chain.length - activeIndex - 1 > 1 ? "s" : ""} locked until this
          decision is made
        </div>
      )}

      {/* Approval Thread */}
      {thread.length > 0 && (
        <div style={sectionDividerStyle}>
          <div
            style={{
              ...sectionHeaderStyle,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <FolderOutlined style={{ fontSize: 14, color: "#475467" }} />
            APPROVAL THREAD
          </div>

        <div style={{ position: "relative", paddingLeft: 20 }}>
          <div
            style={{
              position: "absolute",
              left: 19,
              top: 0,
              bottom: 0,
              width: 2,
              background: "#E4E7EC",
            }}
          />

          {thread.map((entry) => (
            <div
              key={entry.id}
              style={{
                display: "flex",
                gap: 12,
                padding: "16px 0",
                position: "relative",
                marginLeft: 20,
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  background: "#15CCAE",
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 12,
                  fontWeight: 600,
                  flexShrink: 0,
                }}
              >
                {entry.initials}
              </div>
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    flexWrap: "wrap",
                  }}
                >
                  <span style={{ fontWeight: 600, fontSize: 14 }}>
                    {entry.name}{" "}
                    <span style={{ fontWeight: 400, color: "#667085" }}>
                      ({entry.role})
                    </span>
                  </span>
                  {entry.badge && (
                    <span
                      style={{
                        background: entry.badge.bg,
                        color: entry.badge.color,
                        padding: "1px 8px",
                        borderRadius: 10,
                        fontSize: 11,
                        fontWeight: 600,
                      }}
                    >
                      {entry.badge.text}
                    </span>
                  )}
                </div>
                <div
                  style={{
                    fontSize: 14,
                    color: "#344054",
                    marginTop: 4,
                    lineHeight: 1.5,
                  }}
                >
                  {entry.comment}
                </div>
                <div style={{ fontSize: 12, color: "#98A2B3", marginTop: 4 }}>
                  {entry.timestamp}
                </div>
              </div>
            </div>
            ))}
          </div>
        </div>
      )}
    </Form>
  );
};

export default ApprovalWorkflow;
